<template>
  <div class="min-h-screen bg-[#F4F7FE] font-sans text-slate-800">
    <AgentSidebar
      :is-open="isSidebarOpen"
      v-if="auth.agent"
      @close="isSidebarOpen = false"
    />

    <transition name="fade">
      <div
        v-if="isSidebarOpen"
        class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-20 lg:hidden"
        @click="isSidebarOpen = false"
      />
    </transition>

    <div
      class="transition-all duration-300 ease-in-out"
      :class="{ 'lg:ml-64': true, 'ml-0': !isSidebarOpen }"
    >
      <header
        class="bg-white/70 backdrop-blur-lg border-b border-slate-200/50 shadow-sm p-4 flex justify-between items-center fixed top-0 transition-all duration-300 z-30"
        :class="[ isSidebarOpen ? 'lg:w-[calc(100%-16rem)] w-full' : 'w-full' ]"
      >
        <div class="flex items-center gap-4">
          <button @click="isSidebarOpen = !isSidebarOpen" class="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors">
            <Menu class="w-6 h-6" />
          </button>
          
          <h2 class="hidden sm:block text-lg font-bold text-slate-700 tracking-tight">
            Dashboard
          </h2>
        </div>

        <div class="flex items-center gap-3 sm:gap-5">
          <LanguageSwitcher />

          <div v-if="auth.agent?.username" class="hidden sm:flex items-center gap-3 px-3 py-1.5 bg-slate-100 rounded-full border border-slate-200/50">
            <div class="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
              {{ auth.agent.username.charAt(0).toUpperCase() }}
            </div>
            <span class="text-sm font-semibold text-slate-700 pr-1">
              {{ auth.agent.username }}
            </span>
          </div>

          <button
            @click="handleLogout"
            class="group bg-red-50 text-red-500 p-2.5 rounded-xl hover:bg-red-500 hover:text-white hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300 flex items-center justify-center"
            :title="$t('agent.logout')"
          >
            <LogOut class="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
          </button>
        </div>
      </header>

      <main class="pt-[88px] sm:pt-[100px] px-4 pb-4 sm:px-8 sm:pb-8 min-h-screen">
        <transition name="fade-slide" mode="out-in">
          <NuxtPage />
        </transition>
      </main>
    </div>
  </div>
</template>

<script setup>
import AgentSidebar from '~/components/AgentSidebar.vue'
import LanguageSwitcher from '~/components/LanguageSwitcher.vue'
import { Menu, LogOut } from 'lucide-vue-next'
import { ref, onMounted } from 'vue'
import { navigateTo } from '#imports'
import { useAgentAuthStore } from '~/stores/agentAuth'
import { useAgentApi } from '~/composables/useAgentApi'

const isSidebarOpen = ref(true) // 默认大屏开启
const auth = useAgentAuthStore()
const { get, post } = useAgentApi()

onMounted(async () => {
  if (process.client && window.innerWidth < 1024) isSidebarOpen.value = false
  if (auth.agent && auth.sessionChecked) return

  try {
    const res = await get('/agent/me')
    if (res.data && res.data.username) {
      auth.setAgent(res.data)
      auth.setSessionChecked(true)
    } else throw new Error()
  } catch {
    auth.clearAgent()
    auth.setSessionChecked(true)
    navigateTo('/agent/login')
  }
})

const handleLogout = async () => {
  try {
    await post('/agent/logout', {}, { withCredentials: true })
  } finally {
    auth.clearAgent()
    navigateTo('/agent/login')
  }
}
</script>

<style>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateY(10px); }
.fade-slide-leave-to { opacity: 0; transform: translateY(-10px); }
</style>
